import React, { useEffect, useMemo, useState } from "react";
import { FaCalendarAlt, FaClock, FaMapMarkerAlt } from "react-icons/fa";

import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import { supabase } from "../../supabaseClient";

function monthBounds(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);

  const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);

  return {
    start,
    end,
  };
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFullDate(value) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date to be confirmed";
  }

  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function PublicShifts({ session, profile }) {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const [shifts, setShifts] = useState([]);

  const [selectedShift, setSelectedShift] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadShifts() {
      setLoading(true);
      setError("");

      const { start, end } = monthBounds(currentMonth);

      const result = await supabase
        .from("public_shift_calendar")
        .select(
          `
          id,
          title,
          location,
          time
        `,
        )
        .gte("time", start.toISOString())
        .lt("time", end.toISOString())
        .order("time", {
          ascending: true,
        });

      if (!active) {
        return;
      }

      if (result.error) {
        console.error("Unable to load calendar:", result.error);

        setError("The RUHT calendar could not be loaded right now.");

        setShifts([]);
      } else {
        setShifts(result.data || []);
      }

      setLoading(false);
    }

    loadShifts();

    return () => {
      active = false;
    };
  }, [currentMonth]);

  const calendarCells = useMemo(() => {
    const start = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1,
    );

    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0,
    ).getDate();

    const cells = [];

    /*
     * Convert JavaScript Sunday-first
     * indexing to Monday-first indexing.
     */
    const mondayIndex = (start.getDay() + 6) % 7;

    for (let index = 0; index < mondayIndex; index += 1) {
      cells.push(null);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(
        new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day),
      );
    }

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [currentMonth]);

  function shiftsForDay(day) {
    if (!day) {
      return [];
    }

    return shifts.filter((shift) => sameDay(new Date(shift.time), day));
  }

  function previousMonth() {
    setCurrentMonth(
      (value) => new Date(value.getFullYear(), value.getMonth() - 1, 1),
    );
  }

  function nextMonth() {
    setCurrentMonth(
      (value) => new Date(value.getFullYear(), value.getMonth() + 1, 1),
    );
  }

  return (
    <div className="pguh-public-page-shell">
      <PublicHeader session={session} profile={profile} />

      <main
        id="main-content"
        tabIndex="-1"
        className="pguh-public-standard-page"
      >
        <div className="nhsuk-width-container">
          <section className="pguh-public-page-heading">
            <span className="pguh-public-kicker">RUHT calendar</span>

            <h1>Hospital calendar</h1>

            <p>
              View upcoming RUHT sessions, service times and events at our
              sites.
            </p>
          </section>

          <section className="pguh-public-calendar-card">
            <div className="pguh-public-calendar-card__toolbar">
              <button
                type="button"
                onClick={previousMonth}
                aria-label="View previous month"
              >
                Previous month
              </button>

              <h2>
                {currentMonth.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>

              <button
                type="button"
                onClick={nextMonth}
                aria-label="View next month"
              >
                Next month
              </button>
            </div>

            {error && (
              <div className="pguh-public-error" role="alert">
                {error}
              </div>
            )}

            <div className="pguh-public-calendar" aria-busy={loading}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div key={day} className="pguh-public-calendar__weekday">
                  {day}
                </div>
              ))}

              {calendarCells.map((day, index) => {
                const dayShifts = shiftsForDay(day);

                const today = day && sameDay(day, new Date());

                return (
                  <div
                    key={`${day ? day.toISOString() : "empty"}-${index}`}
                    className={`pguh-public-calendar__cell${
                      today ? " pguh-public-calendar__cell--today" : ""
                    }`}
                  >
                    {day && (
                      <>
                        <div className="pguh-public-calendar__date">
                          {day.getDate()}
                        </div>

                        <div className="pguh-public-calendar__events">
                          {dayShifts.map((shift) => (
                            <button
                              type="button"
                              key={shift.id}
                              onClick={() => setSelectedShift(shift)}
                            >
                              <span>{formatTime(shift.time)}</span>

                              <strong>{shift.title}</strong>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {loading && (
              <p className="pguh-public-calendar-loading">Loading calendar…</p>
            )}
          </section>
        </div>
      </main>

      <PublicFooter />

      {selectedShift && (
        <div
          className="pguh-public-modal-backdrop"
          role="presentation"
          onClick={() => setSelectedShift(null)}
        >
          <section
            className="pguh-public-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-calendar-event-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="pguh-public-kicker">RUHT calendar</span>

            <h2 id="public-calendar-event-title">{selectedShift.title}</h2>

            <dl>
              <div>
                <dt>
                  <FaCalendarAlt aria-hidden="true" /> Date
                </dt>

                <dd>{formatFullDate(selectedShift.time)}</dd>
              </div>

              <div>
                <dt>
                  <FaClock aria-hidden="true" /> Time
                </dt>

                <dd>{formatTime(selectedShift.time)}</dd>
              </div>

              <div>
                <dt>
                  <FaMapMarkerAlt aria-hidden="true" /> Location
                </dt>

                <dd>
                  {selectedShift.location ||
                    "Prince George University Hospital"}
                </dd>
              </div>
            </dl>

            <button
              type="button"
              className="nhsuk-button"
              onClick={() => setSelectedShift(null)}
            >
              Close
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
